import React, { useCallback, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import { Classes, Dialog } from "@blueprintjs/core";
import { Search } from "modules/common/components/form/Search";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import { texts } from "modules/common/texts";
import { RoleAccessSnapshotType } from "../../models/access-store";
import { AccessModuleData, matches, withHint } from "../../models/access-tree";
import { AccessCheck } from "../editor/AccessCheck";
import { SectionName } from "../editor/SectionName";
import styles from "./AccessMatrix.module.scss";

const ALL = "";

interface Pending {
    kind: "paste" | "clear";
    roleId: string;
}

/**
 * Матрица ролей: строки — права, колонки — должности, сгруппированные под общей шапкой отдела.
 * Шапка и колонка прав закреплены; подсветка строки и колонки под курсором.
 */
export const AccessMatrix = ({ roles, modules, canChange, onToggle, onReplace }: AccessMatrixProps) => {
    const [query, setQuery] = useState("");
    const [moduleFilter, setModuleFilter] = useState(ALL);
    const [departmentFilter, setDepartmentFilter] = useState(ALL);
    const [buffer, setBuffer] = useState<{ roleId: string; access: string[] } | null>(null);
    const [pending, setPending] = useState<Pending | null>(null);
    const hoverStyle = useRef<HTMLStyleElement | null>(null);

    const departments = useMemo(() => {
        const result: { name: string; roles: RoleAccessSnapshotType[] }[] = [];
        roles.forEach((role) => {
            let dep = result.find((d) => d.name === role.department);
            if (!dep) {
                dep = { name: role.department, roles: [] };
                result.push(dep);
            }
            dep.roles.push(role);
        });
        return result;
    }, [roles]);

    const enabled = useMemo(
        () => new Map(roles.map((r) => [r.id, new Set(r.access.filter((a) => a.value).map((a) => a.name))])),
        [roles]
    );

    // число прав — только тех, что есть в справочнике меток; копируется весь набор должности
    const known = useMemo(() => new Set(modules.flatMap((m) => m.codes)), [modules]);
    const countOf = (roleId: string) => Array.from(enabled.get(roleId) ?? []).filter((c) => known.has(c)).length;

    const shownDepartments = departmentFilter ? departments.filter((d) => d.name === departmentFilter) : departments;
    const columns = shownDepartments.flatMap((d) => d.roles);

    const moduleItems: SelectItem[] = useMemo(
        () => [{ id: ALL, label: "Все модули" }, ...modules.map((m) => ({ id: m.name, label: m.name }))],
        [modules]
    );
    const departmentItems: SelectItem[] = useMemo(
        () => [{ id: ALL, label: "Все отделы" }, ...departments.map((d) => ({ id: d.name, label: d.name }))],
        [departments]
    );

    const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? "";

    // подсветка колонки: одно правило в <style>, а не перерисовка тысяч ячеек на каждое движение мыши
    const onMouseOver = useCallback((e: React.MouseEvent) => {
        const cell = (e.target as HTMLElement).closest("[data-col]") as HTMLElement | null;
        const col = cell?.dataset.col;
        if (hoverStyle.current) {
            hoverStyle.current.textContent = col
                ? `.${styles.matrix} [data-col="${col}"] { background-color: var(--access-matrix-hover) !important; }`
                : "";
        }
    }, []);

    const onMouseLeave = useCallback(() => {
        if (hoverStyle.current) {
            hoverStyle.current.textContent = "";
        }
    }, []);

    const confirm = async () => {
        if (!pending) return;
        const access = pending.kind === "paste" && buffer ? buffer.access : [];
        if (await onReplace(pending.roleId, access)) {
            setPending(null);
        }
    };

    const pendingRole = pending ? roles.find((r) => r.id === pending.roleId) : undefined;

    const rows = modules
        .filter((m) => !moduleFilter || m.name === moduleFilter)
        .map((module) => {
            const items = module.sections.flatMap((s) =>
                s.items
                    .filter((i) => matches(query, module.name, s.name, i.action))
                    .map((i) => ({ section: s.name, ...i }))
            );

            if (items.length === 0) {
                return null;
            }

            return (
                <React.Fragment key={module.name}>
                    <tr className={styles.moduleRow}>
                        <td className={styles.first}>{module.name}</td>
                        <td colSpan={columns.length} />
                    </tr>
                    {items.map((item) => (
                        <tr key={item.code}>
                            <td className={styles.first}>
                                <SectionName name={item.section} items={[item]} />
                                {item.action && <span className={styles.action}> · {item.action}</span>}
                            </td>
                            {columns.map((role) => {
                                const on = !!enabled.get(role.id)?.has(item.code);
                                return (
                                    <td key={role.id} data-col={role.id} className={styles.cell}>
                                        <AccessCheck
                                            state={on ? "on" : "off"}
                                            label={withHint(
                                                `${role.name}: ${item.section}${item.action ? ` · ${item.action}` : ""}`,
                                                item.code
                                            )}
                                            disabled={!canChange}
                                            onClick={() => onToggle(role.id, item.code, !on)}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </React.Fragment>
            );
        });

    return (
        <div className={styles.page}>
            <style ref={hoverStyle} />

            <div className={styles.toolbar}>
                <Search query={query} onSearch={setQuery} placeholder="Поиск права" style={{ marginLeft: 0 }} />
                <div className={styles.select}>
                    <SimpleSelect
                        className="full-width-select"
                        activeItem={moduleItems.find((i) => i.id === moduleFilter)}
                        items={moduleItems}
                        itemRenderer={renderSingleOption}
                        onItemSelect={(i: SelectItem) => setModuleFilter(i.id)}
                        filterable={false}
                        popoverProps={{ popoverClassName: "select-dropdown" }}
                    >
                        <DefaultSelectedOption option={moduleItems.find((i) => i.id === moduleFilter)} />
                    </SimpleSelect>
                </div>
                <div className={styles.select}>
                    <SimpleSelect
                        className="full-width-select"
                        activeItem={departmentItems.find((i) => i.id === departmentFilter)}
                        items={departmentItems}
                        itemRenderer={renderSingleOption}
                        onItemSelect={(i: SelectItem) => setDepartmentFilter(i.id)}
                        filterable={false}
                        popoverProps={{ popoverClassName: "select-dropdown" }}
                    >
                        <DefaultSelectedOption option={departmentItems.find((i) => i.id === departmentFilter)} />
                    </SimpleSelect>
                </div>
                <span className={styles.spacer} />
                {buffer && (
                    <span className={styles.muted}>
                        Скопированы права «{roleName(buffer.roleId)}» — {buffer.access.length}
                    </span>
                )}
            </div>

            <div className={styles.wrap} onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
                <table className={styles.matrix}>
                    <thead>
                        <tr>
                            <th className={classnames(styles.first, styles.corner)} rowSpan={2}>
                                Право
                            </th>
                            {shownDepartments.map((dep) => (
                                <th
                                    key={dep.name}
                                    colSpan={dep.roles.length}
                                    className={styles.department}
                                    title={dep.name}
                                >
                                    <span className={styles.departmentName}>{dep.name}</span>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {columns.map((role) => (
                                <th
                                    key={role.id}
                                    data-col={role.id}
                                    className={classnames(styles.role, { [styles.copied]: buffer?.roleId === role.id })}
                                >
                                    <div className={styles.roleName} title={`${role.name} — ${role.department}`}>
                                        {role.name}
                                    </div>
                                    <div className={styles.muted}>{countOf(role.id)} прав</div>
                                    {canChange && (
                                        <div className={styles.tools}>
                                            <PlanrButton
                                                type="neutral"
                                                size="small"
                                                icon="general-copy"
                                                round={true}
                                                title={texts.copy}
                                                onClick={() =>
                                                    setBuffer({
                                                        roleId: role.id,
                                                        access: Array.from(enabled.get(role.id) ?? []),
                                                    })
                                                }
                                            />
                                            <PlanrButton
                                                type="neutral"
                                                size="small"
                                                icon="general-import"
                                                round={true}
                                                title={texts.paste}
                                                disabled={!buffer || buffer.roleId === role.id}
                                                onClick={() => setPending({ kind: "paste", roleId: role.id })}
                                            />
                                            <PlanrButton
                                                type="neutral"
                                                size="small"
                                                icon="general-trash"
                                                round={true}
                                                title="Очистить"
                                                disabled={countOf(role.id) === 0}
                                                onClick={() => setPending({ kind: "clear", roleId: role.id })}
                                            />
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
                {rows.every((r) => r === null) && (
                    <div className={styles.empty}>Ничего не найдено. Измените запрос или фильтр.</div>
                )}
            </div>

            <Dialog
                isOpen={!!pending}
                onClose={() => setPending(null)}
                title={pending?.kind === "paste" ? "Вставить права" : "Очистить права"}
                className="figma-dialog"
                backdropClassName="standard"
                canOutsideClickClose={false}
                style={{ width: 460 }}
            >
                <div className={Classes.DIALOG_BODY}>
                    {pending && pendingRole && (
                        <>
                            <p>
                                {pending.kind === "paste" && buffer
                                    ? `Права должности «${pendingRole.name}» (${countOf(pendingRole.id)}) будут заменены правами «${roleName(
                                          buffer.roleId
                                      )}» (${buffer.access.length}).`
                                    : `У должности «${pendingRole.name}» будут сняты все права (${countOf(pendingRole.id)}).`}
                            </p>
                            <p className={styles.muted}>Личные исключения сотрудников останутся.</p>
                        </>
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <PlanrButton type="graybtn" size="small" onClick={() => setPending(null)}>
                            {texts.cancel}
                        </PlanrButton>
                        <PlanrButton type="greenish" size="small" onClick={confirm}>
                            {texts.ok}
                        </PlanrButton>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

interface AccessMatrixProps {
    roles: RoleAccessSnapshotType[];
    modules: AccessModuleData[];
    canChange: boolean;
    onToggle: (roleId: string, functionality: string, state: boolean) => void;
    onReplace: (roleId: string, access: string[]) => Promise<boolean>;
}
